import { useState, useEffect } from 'react';
import axios from 'axios';
import { FaWhatsapp } from 'react-icons/fa';

const JobListing = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState(new Set());

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}jobs`);
      setJobs(response.data);
    } catch (error) {
      setError('Error fetching jobs. Please try again later.');
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleSelect = (jobId) => {
    const newSelectedJobs = new Set(selectedJobs);
    if (newSelectedJobs.has(jobId)) {
      newSelectedJobs.delete(jobId);
    } else {
      newSelectedJobs.add(jobId);
    }
    setSelectedJobs(newSelectedJobs);
  };

  const handleSendWhatsApp = (phoneNumber) => {
    const cleanedNumber = phoneNumber.replace(/[^\d]/g, '');
    const whatsappURL = `https://wa.me/${cleanedNumber}`;
    window.open(whatsappURL, '_blank');
  };

  const handleSendEmail = (email) => {
    const mailtoURL = `mailto:${email}`;
    window.open(mailtoURL, '_blank');
  };

  const handleBulkWhatsApp = () => {
    selectedJobs.forEach((jobId) => {
      const job = jobs.find((j) => j._id === jobId);
      if (job.whatsappNumber || job.companyWhatsappNumber) {
        const phoneNumber = job.whatsappNumber || job.companyWhatsappNumber;
        const cleanedNumber = phoneNumber.replace(/[^\d]/g, '');
        window.open(`https://wa.me/${cleanedNumber}`, '_blank');
      }
    });
  };

  const handleBulkEmail = () => {
    const emails = jobs
      .filter((job) => selectedJobs.has(job._id))
      .map((job) => job.email)
      .join(';');
    if (emails) {
      window.open(`mailto:${emails}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start p-4">
      <div className="bg-blue-100 p-6 rounded-t-lg shadow-lg w-full" style={{ marginTop: '64px', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
        <h1 className="text-blue-800 text-2xl font-bold mb-6 text-center">Job Listings & Seekers</h1>
        {loading ? (
          <p className="text-center text-gray-600">Loading...</p>
        ) : error ? (
          <p className="text-center text-red-600">{error}</p>
        ) : jobs.length > 0 ? (
          <>
            <div className="mb-4 flex justify-end space-x-4">
              <button
                onClick={handleBulkWhatsApp}
                className="bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600"
                disabled={selectedJobs.size === 0}
              >
                Send WhatsApp to Selected
              </button>
              <button
                onClick={handleBulkEmail}
                className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600"
                disabled={selectedJobs.size === 0}
              >
                Send Email to Selected
              </button>
            </div>
            <ul className="space-y-6">
              {jobs.map((job) => (
                <li key={job._id} className="p-4 bg-white rounded-lg shadow-md">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-semibold text-blue-800">
                      {job.type.includes('jobseekers') ? 'Job Seeker Application' : 'Job Posting'}
                    </h2>
                    <input
                      type="checkbox"
                      checked={selectedJobs.has(job._id)}
                      onChange={() => handleSelect(job._id)}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <p><strong>Type:</strong> {job.type}</p>
                    {job.type.includes('jobseekers') ? (
                      <>
                        <p><strong>First Name:</strong> {job.firstName}</p>
                        <p><strong>Last Name:</strong> {job.lastName}</p>
                        <p><strong>Email:</strong> <a href="#" onClick={() => handleSendEmail(job.email)} className="text-blue-500 hover:underline">{job.email}</a></p>
                        <p><strong>Phone:</strong> {job.phoneNumber}</p>
                        <p><strong>Whatsapp:</strong> {job.whatsappNumber && (
                          <a href="#" onClick={() => handleSendWhatsApp(job.whatsappNumber)} className="text-green-500 hover:underline flex items-center">
                            <FaWhatsapp className="mr-1" /> {job.whatsappNumber}
                          </a>
                        ) || 'N/A'}</p>
                        <p><strong>Location:</strong> {job.location}</p>
                        <p><strong>Job Type:</strong> {job.jobType}</p>
                      </>
                    ) : (
                      <>
                        <p><strong>Company:</strong> {job.companyName}</p>
                        <p><strong>Company Description:</strong> {job.companyDescription}</p>
                        <p><strong>Company Category:</strong> {job.companyCategory}</p>
                        <p><strong>Position:</strong> {job.position}</p>
                        <p><strong>Contact Name:</strong> {job.lastName}</p>
                        <p><strong>Email:</strong> <a href="#" onClick={() => handleSendEmail(job.email)} className="text-blue-500 hover:underline">{job.email}</a></p>
                        <p><strong>Phone:</strong> {job.phoneNumber}</p>
                        <p><strong>Company Phone:</strong> {job.companyPhoneNumber || 'N/A'}</p>
                        <p><strong>Company Whatsapp:</strong> {job.companyWhatsappNumber && (
                          <a href="#" onClick={() => handleSendWhatsApp(job.companyWhatsappNumber)} className="text-green-500 hover:underline flex items-center">
                            <FaWhatsapp className="mr-1" /> {job.companyWhatsappNumber}
                          </a>
                        ) || 'N/A'}</p>
                        <p><strong>Location:</strong> {job.location}</p>
                        <p><strong>Job Type:</strong> {job.jobType}</p>
                        <p><strong>Job Responsibilities:</strong> {job.jobResponsibilities}</p>
                      </>
                    )}
                    <p><strong>Submitted:</strong> {new Date(job.createdAt).toLocaleString()}</p>
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-center text-gray-600">No jobs or applications found.</p>
        )}
      </div>
    </div>
  );
};

export default JobListing;